import { type DevToolsEnhancerOptions } from '@reduxjs/toolkit';
import { Map } from 'immutable';
import isBoolean from 'lodash/isBoolean';
import merge from 'lodash/merge';
import { type AnyAction } from 'redux';

import { StartupQueryParameterKeys } from '../../constants/App';
import { TREATMENT_ARMS } from '../../constants/Experiments';
import autoMapper from '../../store/utils/autoMapper';
import { type State } from '../../types';
import { type LogLevel } from '../../types/globals/webpack';
import { getUrlQueryParameters } from '../utils/url';

const DEFAULT_DEBUG_SETTINGS = {
  ['version']: __VERSION__,
};

const DEBUG_QUERYSTRING_OVERRIDES = {
  [StartupQueryParameterKeys.LOGGING_LEVEL]: 'logLevel',
  [StartupQueryParameterKeys.LOG_ACTIONS_TO_CONSOLE]: 'logActionsToConsole',
  [StartupQueryParameterKeys.SKIP_STATS]: 'skipStatsLogging',
  [StartupQueryParameterKeys.ENABLE_REDUX_DEVTOOL]: 'reduxDevTool.enabled',
  [StartupQueryParameterKeys.ENABLE_DEBUG_MENU]: 'debugMenuEnabled',
  [StartupQueryParameterKeys.ENABLE_ACTION_GROUP_LOGGER]: 'actionGroupLoggingEnabled',
};

// TODO: How can sanitizers live in their respective domains?
const actionSanitizer = <Action extends AnyAction>(action: Action): AnyAction => {
  const sanitizers = {
    /* #region video */
    VIDEO_PLAYER_INIT: () => ({
      ...action,
      data: {
        ...action.data,
        container: action.data.container.constructor.name,
      },
    }),
    VIDEO_PLAYER_EVENT: () => ({
      ...action,
      data: {
        ...action.data,
        player: action.data.player.constructor.name,
      },
    }),
    VIDEO_PLAYER_RENDERED: () => ({
      ...action,
      data: {
        ...action.data,
        container: action.data.container.constructor.name,
        video: action.data.video.constructor.name,
      },
    }),
    VIDEO_PLAYER_STATE: () => ({
      ...action,
      data: {
        ...action.data,
        mediaPlayerRef: action.data.mediaPlayerRef?.constructor.name,
      },
    }),
    /* #endregion */
  };

  return action.type in sanitizers ? sanitizers[action.type as keyof typeof sanitizers]() : action;
};

// TODO: How can sanitizers live in their respective domains?
const stateSanitizer = (state: State): State => {
  return state
    ? merge({}, state, {
        ...(state.video
          ? {
              video: {
                state: state.video.state
                  ? {
                      mediaPlayerRef: state.video.state.mediaPlayerRef?.constructor.name,
                    }
                  : undefined,
              },
            }
          : undefined),
      })
    : state;
};

function parseBoolean(val: boolean | string): boolean {
  if (isBoolean(val)) {
    return val;
  }
  return val.toLowerCase() === 'true' ? true : false;
}

const reduxDevToolSanitizers = {
  reduxDevTool: { actionSanitizer, stateSanitizer } as DevToolsEnhancerOptions,
};
const queryStringOverrides = autoMapper(getUrlQueryParameters(), DEBUG_QUERYSTRING_OVERRIDES);
const debugSettings = merge(
  DEFAULT_DEBUG_SETTINGS,
  __DEBUG_SETTINGS__,
  queryStringOverrides,
  reduxDevToolSanitizers,
);

export const getAudioLogLevel = (): LogLevel => debugSettings.audioLogLevel;
export const getLogLevel = (): LogLevel => debugSettings.logLevel;
export const getTestCoachmarksType = (): string => debugSettings.testCoachmarksType;
export const getVideoLogLevel = (): LogLevel => debugSettings.videoLogLevel;
export const isDebugMenuEnabled = (): boolean => {
  // allow debug menu to be enabled via querystring everywhere but production
  return (
    !/pandora.com/.test(window.location.origin) && parseBoolean(debugSettings.debugMenuEnabled)
  );
};
export const isStorageObscured = (): boolean => debugSettings.obscureStorage;
export const shouldLogActionsToConsole = (): boolean =>
  parseBoolean(debugSettings.logActionsToConsole);
export const shouldTrapErrors = (): boolean => debugSettings.trapErrors;
export const version = (): string => debugSettings.version;
export const shouldSkipStatsLogging = (): boolean => parseBoolean(debugSettings.skipStatsLogging);
export const isReduxDevtoolEnabled = (): boolean =>
  parseBoolean(debugSettings.reduxDevTool.enabled);
export const actionGroupLoggingEnabled = (): boolean =>
  parseBoolean(debugSettings.actionGroupLoggingEnabled);
export const actionGroupLoggingFilter = (): string[] => debugSettings.actionGroupLoggingFilter;
export const getReduxDevtoolOptions = (): typeof debugSettings['reduxDevTool'] =>
  debugSettings.reduxDevTool;

export const expToNameMap = Map(TREATMENT_ARMS).flip().toJS();
